import React, { useEffect, useState } from 'react';

import { Input, Modal, toastService } from '@thefarmersfront/kitchen';

import { FormField } from '~/components/common';

import { usePatchOwnerManagerModify } from '~/hooks/vender';
import { useLoadingStore } from '~/store';
import {
  validateEmailFormat,
  validateNumberAndHyphenOnly,
  validateTelFormat,
  validateVenderManagerName,
} from '~/utils';

import { DEFAULT_INPUT_STATUS, TOAST_MESSAGE } from '~/constants';

import type { TypeErrorResponseData, TypeInputState } from '~/types/common.types';
import type { TypeOwnerManagerDetail } from '~/types/vender.types';

import styles from './VenderOwnerManagerModifyModal.module.scss';

type TypeVenderOwnerManagerModifyModalProps = {
  open: boolean;
  managerInfo: TypeOwnerManagerDetail | null;
  onCancel: () => void;
  onConfirm: () => void;
};

const DEFAULT_MANAGER_MODIFY_FORM = {
  id: '',
  tempPassword: '',
  name: '',
  position: '',
  tel: '',
  email: '',
};

/**
 * [화주사 정보 관리 > 화주사 정보] 담당자 정보 수정 모달 컴포넌트
 */
const VenderOwnerManagerModifyModal = ({
  open,
  managerInfo,
  onCancel,
  onConfirm,
}: TypeVenderOwnerManagerModifyModalProps) => {
  const { patchOwnerManagerModify } = usePatchOwnerManagerModify();

  const { setIsLoading } = useLoadingStore();

  const [managerModifyForm, setManagerModifyForm] = useState(DEFAULT_MANAGER_MODIFY_FORM);

  const [nameStatus, setNameStatus] = useState<TypeInputState>(DEFAULT_INPUT_STATUS);
  const [telStatus, setTelStatus] = useState<TypeInputState>(DEFAULT_INPUT_STATUS);
  const [emailStatus, setEmailStatus] = useState<TypeInputState>(DEFAULT_INPUT_STATUS);

  useEffect(() => {
    if (open) {
      initManagerModifyForm();
    }
  }, [open]);

  const fetchOwnerManagerModify = async () => {
    try {
      setIsLoading(true);

      await patchOwnerManagerModify({
        ownerLoginId: managerInfo?.ownerLoginId ?? '',
        ownerLoginPwd: managerModifyForm.tempPassword.trim(),
        ownerManagerName: managerModifyForm.name.trim(),
        ownerManagerPosition: managerModifyForm.position.trim(),
        ownerManagerTel: managerModifyForm.tel.trim(),
        ownerManagerEmail: managerModifyForm.email.trim(),
      });

      toastService.addToast({
        message: '성공적으로 저장되었습니다.',
      });

      onConfirm();
    } catch (error) {
      toastService.addToast({
        type: 'error',
        message: (error as TypeErrorResponseData['error'])?.message || TOAST_MESSAGE.DEFAULT_ERROR,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const initManagerModifyForm = () => {
    setManagerModifyForm({
      id: managerInfo?.ownerLoginId ?? '',
      tempPassword: '',
      name: managerInfo?.ownerManagerName ?? '',
      position: managerInfo?.ownerManagerPosition ?? '',
      tel: managerInfo?.ownerManagerTel ?? '',
      email: managerInfo?.ownerManagerEmail ?? '',
    });

    setNameStatus(DEFAULT_INPUT_STATUS);
    setTelStatus(DEFAULT_INPUT_STATUS);
    setEmailStatus(DEFAULT_INPUT_STATUS);
  };

  const handleFocusManagerName = () => setNameStatus(DEFAULT_INPUT_STATUS);

  const handleFocusManagerTel = () => setTelStatus(DEFAULT_INPUT_STATUS);

  const handleFocusManagerEmail = () => setEmailStatus(DEFAULT_INPUT_STATUS);

  const handleChangeTempPassword = (e: React.ChangeEvent<HTMLInputElement>) =>
    setManagerModifyForm({ ...managerModifyForm, tempPassword: e.target.value });

  const handleChangeManagerName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valueWithoutSpaces = e.target.value.replace(/\s+/g, '');
    setManagerModifyForm({ ...managerModifyForm, name: valueWithoutSpaces });
  };

  const handleChangeManagerPosition = (e: React.ChangeEvent<HTMLInputElement>) =>
    setManagerModifyForm({ ...managerModifyForm, position: e.target.value });

  const handleChangeManagerTel = (e: React.ChangeEvent<HTMLInputElement>) => {
    const updatedValue = e.target.value;

    if (validateNumberAndHyphenOnly(updatedValue)) {
      setManagerModifyForm({ ...managerModifyForm, tel: updatedValue });
    }
  };

  const handleChangeManagerEmail = (e: React.ChangeEvent<HTMLInputElement>) =>
    setManagerModifyForm({ ...managerModifyForm, email: e.target.value });

  const validateForm = (): boolean => {
    let isValidForm = true;

    if (managerModifyForm.name.trim() === '') {
      setNameStatus({ status: 'error', message: '필수 입력입니다.' });
      isValidForm = false;
    } else if (!validateVenderManagerName(managerModifyForm.name.trim())) {
      setNameStatus({ status: 'error', message: '올바른 형식으로 입력해주세요.' });
      isValidForm = false;
    }

    if (managerModifyForm.tel.trim() === '') {
      setTelStatus({ status: 'error', message: '필수 입력입니다.' });
      isValidForm = false;
    } else if (!validateTelFormat(managerModifyForm.tel.trim())) {
      setTelStatus({ status: 'error', message: '올바른 형식으로 입력해주세요.' });
      isValidForm = false;
    }

    if (managerModifyForm.email.trim() === '') {
      setEmailStatus({ status: 'error', message: '필수 입력입니다.' });
      isValidForm = false;
    } else if (!validateEmailFormat(managerModifyForm.email.trim())) {
      setEmailStatus({ status: 'error', message: '올바른 형식으로 입력해주세요.' });
      isValidForm = false;
    }

    return isValidForm;
  };

  const handleConfirm = () => {
    const isValidForm = validateForm();

    if (isValidForm) {
      fetchOwnerManagerModify();
    }
  };

  return (
    <Modal
      open={open}
      title="담당자 정보 수정"
      confirmText="저장"
      confirmButtonType="submit"
      onCancel={onCancel}
      onConfirm={handleConfirm}
    >
      <FormField
        label="아이디"
        required
        contents={<Input type="text" size="small" disabled value={managerInfo?.ownerLoginId ?? ''} />}
        labelClassName={styles['label']}
      />
      <FormField
        label="임시 비밀번호"
        contents={
          <Input
            type="password"
            size="small"
            placeholder="비밀번호 변경 시 입력"
            autoComplete="new-password"
            value={managerModifyForm.tempPassword}
            onChange={handleChangeTempPassword}
          />
        }
        labelClassName={styles['label']}
      />
      <FormField
        label="담당자명"
        required
        contents={
          <Input
            type="text"
            size="small"
            placeholder="입력"
            status={nameStatus.status}
            validationMessage={nameStatus.message}
            value={managerModifyForm.name}
            onFocus={handleFocusManagerName}
            onChange={handleChangeManagerName}
          />
        }
        labelClassName={styles['label']}
      />
      <FormField
        label="직위"
        contents={
          <Input
            type="text"
            size="small"
            placeholder="입력"
            value={managerModifyForm.position}
            onChange={handleChangeManagerPosition}
          />
        }
        labelClassName={styles['label']}
      />
      <FormField
        label="휴대폰번호"
        required
        contents={
          <Input
            type="text"
            size="small"
            placeholder="- 포함 입력"
            status={telStatus.status}
            validationMessage={telStatus.message}
            value={managerModifyForm.tel}
            onFocus={handleFocusManagerTel}
            onChange={handleChangeManagerTel}
          />
        }
        labelClassName={styles['label']}
      />
      <FormField
        label="이메일"
        required
        contents={
          <Input
            className={styles['input']}
            type="text"
            size="small"
            placeholder="example@kls.com"
            status={emailStatus.status}
            validationMessage={emailStatus.message}
            value={managerModifyForm.email}
            onFocus={handleFocusManagerEmail}
            onChange={handleChangeManagerEmail}
          />
        }
        labelClassName={styles['label']}
      />
    </Modal>
  );
};

export default VenderOwnerManagerModifyModal;
